import { AlertCircle } from "lucide-react";
import React, { useState } from "react";
import { useQueryState } from "nuqs";
import {
  getUSCountry,
  validatePostalCode,
  type Country,
} from "../utils/countryUtils";
import { CountrySelect } from "./CountrySelect";
import { getCountryByCode } from "../utils/validationUtils";

/**
 * Props for the LocationSearch component
 * @interface LocationSearchProps
 * @property {(country: string, postalCode: string) => void} onSubmit - Callback function when the form is submitted
 * @property {string} [buttonText="Start matching"] - Text to display on the submit button
 * @property {Country} [initialCountry] - Initial country selection, defaults to user's locale
 * @property {string} [initialPostalCode=""] - Initial postal code value
 */
interface LocationSearchProps {
  onSubmit: (country: string, postalCode: string) => void;
  buttonText?: string;
  initialCountry?: Country;
  initialPostalCode?: string;
}

/**
 * A form component for collecting location information with country selection and postal code input.
 * Features client-side validation, formatting, and error handling.
 *
 * @component
 * @example
 * ```tsx
 * <LocationSearch
 *   onSubmit={(country, postalCode) => {
 *     console.log(`Location submitted: ${country}, ${postalCode}`);
 *   }}
 *   buttonText="Find Trials"
 * />
 * ```
 */
export function LocationSearch({
  onSubmit,
  buttonText = "Start matching",
  initialPostalCode = "",
  initialCountry = getUSCountry(),
}: LocationSearchProps) {
  const [zipCode, setZipCode] = useState(initialPostalCode);
  const [error, setError] = useState("");

  // Use nuqs to manage country state in URL search params
  const [countryCode, setCountryCode] = useQueryState("country", {
    defaultValue: initialCountry.code,
  });

  const selectedCountry = getCountryByCode(countryCode);

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setZipCode(value);
    setError("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePostalCode(selectedCountry, zipCode)) {
      setError(
        `Please enter a valid ${selectedCountry.postalCodeLabel.toLowerCase()} (e.g., ${selectedCountry.postalCodeExample})`,
      );
      return;
    }
    onSubmit(selectedCountry.code, zipCode);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col items-center w-full">
      <div className="flex flex-col sm:flex-row w-full space-y-4 sm:space-y-0">
        <div className="flex w-full sm:flex-1">
          <div className="w-28">
            <CountrySelect
              selected={selectedCountry}
              onSelect={(country) => {
                setCountryCode(country.code);
                setZipCode("");
                setError("");
              }}
            />
          </div>
          <div className="flex-1 flex flex-col">
            <input
              type="text"
              value={zipCode}
              onChange={handleZipCodeChange}
              placeholder={`${selectedCountry.postalCodeLabel}`}
              className={`w-full px-6 py-3 border-2 focus:outline-none focus:ring-2 focus:ring-offset-0 rounded-r-lg sm:rounded-r-none h-full ${
                error
                  ? "border-red-500 focus:ring-red-500/50"
                  : "border-transparent focus:ring-[#FF6B6B]/50"
              } bg-white text-base placeholder-gray-400`}
              aria-label={`Enter your ${selectedCountry.postalCodeLabel}`}
              maxLength={10}
              required
            />
          </div>
        </div>
        <button
          type="submit"
          className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-neutral-800 to-neutral-900 text-white font-medium hover:from-neutral-900 hover:to-neutral-800 transition-all duration-300 rounded-lg sm:rounded-l-none flex items-center justify-center whitespace-nowrap shadow-lg shadow-neutral-900/20"
        >
          {buttonText}
        </button>
      </div>
      {error && (
        <div className="flex items-center text-red-500 text-sm mt-2">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error}
        </div>
      )}
    </form>
  );
}
